import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import { api, errMsg, API } from "../../lib/api";
import PublicFrame, { usePublicBranding } from "./PublicFrame";

const HEALTH = [["allergies", "Allergies"], ["medications", "Medications"], ["conditions", "Conditions"], ["blood_type", "Blood type"],
  ["emergency_contact", "Emergency contact"], ["gp_contact", "Family doctor / GP"]];

const Card = ({ title, children, testId }) => (
  <section className="rounded-[var(--radius)] border bg-card p-5 space-y-3" data-testid={testId}><h2 className="text-xl">{title}</h2>{children}</section>
);

export function PassportBody({ p, photoUrl }) {
  const pr = p.profile;
  return (
    <div className="space-y-4">
      <Card title={`${pr.first_name} ${pr.last_name || ""}`} testId="passport-profile">
        <div className="text-sm text-muted-foreground">{pr.dob ? `Born ${pr.dob}` : ""}{pr.gender ? ` · ${pr.gender}` : ""} · ID {pr.client_code}</div>
      </Card>
      {p.health && <Card title="Health summary" testId="passport-health">
        <dl className="grid sm:grid-cols-2 gap-3 text-sm">{HEALTH.map(([k, l]) => (
          <div key={k}><dt className={`text-xs font-semibold ${k === "allergies" && p.health[k] ? "text-destructive" : "text-muted-foreground"}`}>{l}</dt><dd className="whitespace-pre-wrap">{p.health[k] || "—"}</dd></div>))}</dl>
      </Card>}
      {p.treatments && <Card title="Treatment history" testId="passport-treatments">
        {!p.treatments.length ? <p className="text-sm text-muted-foreground">No treatments yet</p> : p.treatments.map((t, i) => (
          <div key={i} className="flex flex-wrap gap-3 text-sm border-b last:border-0 pb-2"><span className="font-mono w-24">{t.date}</span><span className="flex-1">{t.service_name}</span><span className="text-muted-foreground text-xs">{t.professional_name} · {t.location_name}</span></div>))}
      </Card>}
      {p.notes && <Card title="Clinical notes" testId="passport-notes">
        {!p.notes.length ? <p className="text-sm text-muted-foreground">No shared notes</p> : p.notes.map((n) => (
          <div key={n.id} className="space-y-1.5 border-b last:border-0 pb-3"><div className="text-sm font-semibold">{n.template_name} <span className="text-xs font-normal text-muted-foreground">· {n.created_at.slice(0, 10)} · {n.author_name}</span></div>
            {n.sections.filter((s) => n.values[s.key]).map((s) => <div key={s.key} className="text-sm"><span className="text-xs font-semibold text-muted-foreground">{s.label.split(" – ")[0]}: </span><span className="whitespace-pre-wrap">{n.values[s.key]}</span></div>)}</div>))}
      </Card>}
      {p.consents && <Card title="Signed consent forms" testId="passport-consents">
        {!p.consents.length ? <p className="text-sm text-muted-foreground">None</p> : p.consents.map((c, i) => <div key={i} className="text-sm">{c.template_name} · <span className="text-muted-foreground">{(c.signed_at || "").slice(0, 10)}</span></div>)}
      </Card>}
      {p.photos && photoUrl && <Card title="Before & after photos" testId="passport-photos">
        {!p.photos.length ? <p className="text-sm text-muted-foreground">None</p> : <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">{p.photos.map((ph) => (
          <figure key={ph.id}><img src={photoUrl(ph.id)} alt={ph.kind} className="aspect-square w-full object-cover rounded-md border" /><figcaption className="text-[10px] text-center capitalize">{ph.kind} · {ph.area} · {ph.taken_at.slice(0, 10)}</figcaption></figure>))}</div>}
      </Card>}
    </div>
  );
}

export default function PassportView() {
  const { token } = useParams();
  const [p, setP] = useState(null);
  const [err, setErr] = useState("");
  usePublicBranding(p);
  useEffect(() => { api.get(`/public/passport/${token}`).then((r) => setP(r.data)).catch((e) => setErr(errMsg(e))); }, [token]);
  if (err) return <PublicFrame><p className="py-16 text-center" data-testid="passport-error">{err}</p></PublicFrame>;
  if (!p) return <PublicFrame><p>…</p></PublicFrame>;
  return (
    <PublicFrame branding={p.branding}>
      <div className="space-y-5" data-testid="passport-view">
        <div><p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Health & Beauty Passport</p><h1 className="text-3xl sm:text-4xl">Shared patient record</h1>
          <p className="text-sm text-muted-foreground mt-2 flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-primary" />Shared by the patient · read-only · access expires {new Date(p.expires_at).toLocaleString()}</p></div>
        <PassportBody p={p} photoUrl={(id) => `${API}/public/passport/${token}/photo/${id}`} />
      </div>
    </PublicFrame>
  );
}
